import React from 'react';
import { Navigate } from 'react-router-dom';
import { FaLock } from 'react-icons/fa';
import { useFeatures } from '../../context/FeatureContext';

/**
 * FeatureRoute component that conditionally renders a route based on feature configuration
 * If the feature is disabled, it redirects to the settings page or first available route
 */
const FeatureRoute = ({ 
  feature, 
  children, 
  fallbackPath = '/settings',
  showDisabledMessage = false 
}) => {
  const { isFeatureEnabled, getEnabledFeatures } = useFeatures();

  // If no feature is specified, always show the route
  if (!feature) {
    return children;
  }

  // If feature is enabled, show the route
  if (isFeatureEnabled(feature)) {
    return children;
  }

  // Feature is disabled - determine fallback behavior
  if (showDisabledMessage) {
    return (
      <div className="feature-disabled-message">
        <div className="message-container">
          <FaLock />
          <h2>Feature Not Available</h2>
          <p>This feature has been disabled by the system administrator.</p>
          <p>Contact your administrator if you need access to this feature.</p>
          <div className="available-features">
            <h3>Available Features:</h3>
            <ul>
              {getEnabledFeatures().map(featureName => (
                <li key={featureName}>{featureName}</li>
              ))}
            </ul>
          </div>
        </div>
        <style jsx>{`
          .feature-disabled-message {
            display: flex;
            align-items: center;
            justify-content: center;
            min-height: 400px;
            padding: 40px;
            text-align: center;
          }
          
          .message-container {
            max-width: 500px;
            background: #f8f9fa;
            padding: 40px;
            border-radius: 8px;
            border: 1px solid #dee2e6;
          }
          
          .message-container i {
            font-size: 48px;
            color: #6c757d;
            margin-bottom: 20px;
          }
          
          .message-container h2 {
            color: #495057;
            margin-bottom: 15px;
          }
          
          .message-container p {
            color: #6c757d;
            margin-bottom: 10px;
            line-height: 1.5;
          }
          
          .available-features {
            margin-top: 30px;
            text-align: left;
          }
          
          .available-features h3 {
            color: #495057;
            margin-bottom: 15px;
            font-size: 16px;
          }
          
          .available-features ul {
            list-style: none;
            padding: 0;
          }
          
          .available-features li {
            padding: 8px 0;
            border-bottom: 1px solid #e9ecef;
            color: #495057;
            text-transform: capitalize;
          }
          
          .available-features li:last-child {
            border-bottom: none;
          }
        `}</style>
      </div>
    );
  }

  // Redirect to fallback path
  return <Navigate to={fallbackPath} replace />;
};

export default FeatureRoute;